import PouchDB from 'pouchdb'
import sqlite from 'pouchdb-adapter-cordova-sqlite'
import find from 'pouchdb-find'
import debug from 'pouchdb-debug'
import { LoadingBar, Notify } from 'quasar'
import { Promise } from 'bluebird'
import { chunk, orderBy } from 'lodash'
import dayjs from 'src/boot/dayjs'

import { i18n } from './i18n'
const { t } = i18n.global

const MAX_CONSECUTIVE_ERRORS = 10
import { boot } from 'quasar/wrappers'

import eventBus from 'src/eventBus'
import { NotificationService } from 'src/services/NotificationService'

const WIP_DURATION_LIMIT = 5 // minutes
// use i18n

PouchDB.plugin(find)
PouchDB.plugin(debug)
PouchDB.debug.enable('pouchdb:find')

export default boot(({ app }) => {
  var localIssues = null
  var localProviders = null
  var tmpReports = null
  var tmpIssues = null
  var tmpPlaces = null
  var remoteIssues = null
  var replicationHandler = null
  var isCordova = false
  var localUsers = null

  const deleteTmpReport = (tmpReport) => {
    return tmpReports.get(tmpReport._id).then((doc) => {
      return tmpReports.remove(doc)
    })
  }

  const markTmpReportDone = (tmpReport) => {
    return tmpReports.get(tmpReport._id).then((doc) => {
      doc.status = 'DONE'
      doc.doneAt = new Date().toISOString().split('T')[0]
      return tmpReports.put(doc)
    })
  }

  const deleteTmpIssue = (tmpIssue) => {
    return tmpIssues.get(tmpIssue._id).then((doc) => {
      return tmpIssues.remove(doc)
    })
  }

  const _commitTmpIssue = async (tmpIssue, proxy, store) => {
    let localReportId
    let tmpReport

    if ('report' in tmpIssue && tmpIssue.report.localId) {
      tmpReport = await tmpReports.get(tmpIssue.report.localId)
      if (!tmpReport.remoteId) return
      localReportId = tmpReport._id
      tmpIssue.report.id = tmpReport.remoteId
    }

    await proxy
      .createIssue(tmpIssue)
      .then((response) => {
        if (tmpIssue.toForward) {
          proxy
            .forwardIssue(response.data.id, { data: { type: 'issues' } })
            .then(
              () => {
                app.config.globalProperties.$q.notify({
                  message: t('issueDetails.forwardIssuesSuccess'),
                  color: 'info',
                  position: 'top'
                })
              },
              () => {
                app.config.globalProperties.$q.notify({
                  message: t('issueDetails.forwardIssuesFailed'),
                  color: 'warning',
                  position: 'top'
                })
              }
            )
        }
      })
      .then(async () => {
        if ('report' in tmpIssue && tmpReport.remoteId) {
          const result = await tmpIssues.find({
            selector: {
              'report.localId': {
                $eq: localReportId
              }
            }
          })
          if (result.docs.length === 1) {
            store.dispatch('audits/close', tmpReport.audit)
            await markTmpReportDone(tmpReport)
          }
        }
      })
      .then(async () => {
        await deleteTmpIssue(tmpIssue)
      })
      .catch(async (e) => {
        console.error(
          '🚀 ~ file: pouchDB.js:98 ~ const_commitTmpIssue= ~ e:',
          e
        )
        if (
          e[0] &&
          e[0]?.title &&
          (e[0]?.title.startsWith('duplicate detected') ||
            e[0]?.title.startsWith('Access forbiden'))
        ) {
          deleteTmpIssue(tmpIssue)
        } else if (e[0]?.title === 'Resource not found') {
          const placesCollection =
            app.config.globalProperties.$patrimoine.placesCollection()
          const place = await placesCollection.get(tmpIssue.place.id)
          place.spots = place.spots.filter(
            (spot) => spot.id !== tmpIssue.spot.id
          )
          if (!place.spots.length) {
            await app.config.globalProperties.$patrimoine.deletePlaceCascade(
              place,
              ['residence', 'agency']
            )
          } else {
            await placesCollection.put(place)
          }
          deleteTmpIssue(tmpIssue)
        } else if (e.message === 'Network Error'){
          tmpIssue.wip = false
          // last changes on current tmpIssue, no need to update _rev
          tmpIssues.put(tmpIssue)
        }
        // other known errors here
        else {
          deleteTmpIssue(tmpIssue)
        }
      })
  }

  const _commitTmpTalks = async (tmpIssue, proxy) => {
    const issueId = tmpIssue._id.split(':')[0]
    for (const talk of tmpIssue.tracking.talks) {
      await proxy
        .createTalk(issueId, talk.message)
        .then(() => {
          let filtered = tmpIssue.tracking.talks.filter((el) => {
            return el.id !== talk.id
          })
          tmpIssue.tracking.talks = filtered
          tmpIssues.put(tmpIssue)
        })
        .catch(async (e) => {
          if (
            e[0] &&
            e[0]?.title &&
            (e[0]?.title.startsWith('duplicate detected') ||
              e[0]?.title.startsWith('Resource not found') ||
              e[0]?.title.startsWith('Access forbiden'))
          ) {
            app.config.globalProperties.$q.notify({
              message: t('common.error.unknownErrorLastAction'),
              color: 'warning',
              position: 'top'
            })
            deleteTmpIssue(tmpIssue)
          } else {
            tmpIssue.wip = false
            // last changes on current tmpIssue, no need to update _rev
            tmpIssues.put(tmpIssue)
          }
        })
    }

    if (tmpIssue.tracking.talks.length === 0) {
      deleteTmpIssue(tmpIssue)
    }
  }

  const _commitTmpClose = async (tmpIssue, proxy) => {
    const issueId = tmpIssue._id.split(':')[0]
    await proxy
      .updateIssue({
        id: issueId,
        status: tmpIssue.tracking.status
      })
      .then((apiResponse) => {
        // NOTE: we only update the localIssue when tmpIssue was sent to the API successfully
        localIssues
          .find({
            selector: { _id: issueId },
            limit: 1
          })
          .then(async (results) => {
            if (results.docs.length) {
              const localIssue = results.docs[0]
              localIssue.tracking.status = 'done'
              localIssue.event = 'close'
              localIssue.meta.updatedAt = apiResponse.data['updated-at']
              try {
                const updateResult = await localIssues.put(localIssue)
                const change = {
                  id: updateResult.id,
                  changes: [{ _rev: updateResult.rev }],
                  doc: { ...localIssue, _rev: updateResult.rev }
                }
                eventBus.$emit('localIssueChange', change)
              } catch (err) {
                console.error('err localIssues.put', err)
              }
            } else {
              console.warn('cannot find localIssue with issueId', issueId)
            }
          })
      })
      .catch((error) => {
        console.error('error syncing local close to api', error)
        if (error.length && (error[0]?.title || '')) {
          if (error[0] && error[0]?.title === 'Access forbiden') {
            Notify.create({
              message: t('issueDetails.accessForbidden'),
              color: 'warning',
              position: 'top'
            })
            // NOTE: we delete the tmpIssue without updating the localIssue in case of an error
          } else if (
            error[0] &&
            error[0]?.title &&
            error[0]?.title.startsWith('duplicate detected')
          ) {
            Notify.create({
              message: t('issueDetails.duplicateError'),
              color: 'warning',
              position: 'top'
            })
          } else {
            Notify.create({
              message: t('issueDetails.unknownError'),
              color: 'warning',
              position: 'top'
            })
          }
        } else {
          Notify.create({
            message: t('issueDetails.closeError'),
            color: 'warning',
            position: 'top'
          })
        }
      })
      .finally(() => {
        deleteTmpIssue(tmpIssue)
      })
  }

  const _commitTmpCode = async (tmpIssue, proxy) => {
    const issueId = tmpIssue._id.split(':')[0]
    await proxy
      .updateIssue({
        id: issueId,
        code: tmpIssue.tracking.code
      })
      .then((apiResponse) => {
        // NOTE: we only update the localIssue when tmpIssue was sent to the API successfully
        localIssues
          .find({
            selector: { _id: issueId },
            limit: 1
          })
          .then(async (results) => {
            if (results.docs.length) {
              const localIssue = results.docs[0]
              localIssue.tracking.code = tmpIssue.tracking.code
              localIssue.meta.updatedAt = apiResponse.data['updated-at']
              try {
                const updateResult = await localIssues.put(localIssue)
                const change = {
                  id: updateResult.id,
                  changes: [{ _rev: updateResult.rev }],
                  doc: { ...localIssue, _rev: updateResult.rev }
                }
                eventBus.$emit('localIssueChange', change)
              } catch (err) {
                console.error('err localIssues.put', err)
              }
            } else {
              console.warn('cannot find localIssue with issueId', issueId)
            }
          })
      })
      .catch((error) => {
        console.error('error syncing local code to api', error)
        if (error.length && (error[0]?.title || '')) {
          if (error[0] && error[0]?.title === 'Access forbiden') {
            Notify.create({
              message: t('issueDetails.codePermissionError'),
              color: 'warning',
              position: 'top'
            })
            // NOTE: we delete the tmpIssue without updating the localIssue in case of an error
          } else {
            Notify.create({
              message: t('issueDetails.unknownError'),
              color: 'warning',
              position: 'top'
            })
          }
        } else {
          Notify.create({
            message: t('issueDetails.codeError'),
            color: 'warning',
            position: 'top'
          })
        }
      })
      .finally(() => {
        deleteTmpIssue(tmpIssue)
      })
  }

  const syncTmpIssues = async (issuesToSync, proxy, store) => {
    return new Promise(async (resolve, reject) => {
      // NOTE: send tmpIssues
      if (issuesToSync.length === 0) {
        store.dispatch('common/clearPendingIssues')
        return resolve()
      } else {
        store.dispatch('common/setPendingIssues', issuesToSync.length)
        try {
          await Promise.each(issuesToSync, async (tmpIssue) => {
            await helpers.processTmpIssue(tmpIssue, proxy, store)
          })
          store.dispatch('common/clearPendingIssues')
          return resolve()
        } catch (e) {
          console.error('syncReportsJob issues creation error detected')
          return reject(e)
        }
      }
    })
  }

  const helpers = {
    initRemote: (username, password, DB) => {
      let URI =
        'https://' +
        username +
        ':' +
        password +
        '@5b209f9d-f355-4198-8ba2-896b990b307d-bluemix.cloudantnosqldb.appdomain.cloud/' +
        DB
      let remoteOptions = { name: 'remoteCmpIssues', skip_setup: true }
      remoteIssues = new PouchDB(URI, remoteOptions)
    },

    processTmpReport: async (tmpReport, proxy, store) => {
      if (tmpReport.status === 'IN_PROGRESS') {
        if (tmpReport.remoteId) {
          // NOTE: skip because tmpReport is waiting for his issues to be sent
          return
        }
        // NOTE: tmpReport is already being processed but not sent to the API
        if (!tmpReport.lastAttempted) {
          tmpReport.status = 'PENDING'
          await tmpReports.put(tmpReport)
          return
        }

        const lastAttempted = dayjs(
          tmpReport.lastAttempted,
          'DD/MM/YYYY HH:mm:ss'
        )
        const diff = dayjs().diff(lastAttempted, 'minute')
        if (diff > WIP_DURATION_LIMIT) {
          tmpReport.status = 'PENDING'
          await tmpReports.put(tmpReport)
        }
        return
      }
      tmpReport.status = 'IN_PROGRESS'
      tmpReport.lastAttempted = dayjs().format('DD/MM/YYYY HH:mm:ss')
      const result = await tmpReports.put(tmpReport)
      if (result.ok) {
        tmpReport._rev = result.rev
      }
      return new Promise((resolve, reject) => {
        proxy
          .createReport(tmpReport)
          .then(async (response) => {
            const report = response.data
            try {
              const result = await tmpIssues.find({
                selector: {
                  'report.localId': {
                    $eq: tmpReport._id
                  }
                }
              })
              if (result.docs.length === 0) {
                store.dispatch('audits/close', tmpReport.audit)
                await markTmpReportDone(tmpReport)
              } else {
                if (report && report.id) {
                  tmpReport.remoteId = report.id
                  const res = await tmpReports.put(tmpReport)
                  if (res.ok) {
                    tmpReport._rev = res.rev
                  }
                  await Promise.each(result.docs, async (tmpIssue) => {
                    tmpIssue.report.id = report.id
                    await tmpIssues.put(tmpIssue)
                  })
                }
                const _tmpIssues = result.docs.map((tmpIssue) => {
                  tmpIssue.report.id = report.id
                  return tmpIssue
                })
                try {
                  await tmpIssues.bulkDocs(_tmpIssues)
                } catch (e) {
                  console.error('error while updating tmpIssues reportid')
                } finally {
                  await syncTmpIssues(_tmpIssues, proxy, store)
                }
              }
              return resolve()
            } catch (e) {
              console.error('syncReportsJob issues creation failed', e)
              return resolve()
            }
          })
          .catch(async (e) => {
            const title = e[0] && e[0]?.title ? e[0]?.title : ''
            // NOTE: on retryable errors the POST may have reached the API even
            // though the client got no response — keep the tmpReport (and its
            // uid used by the server-side duplicate detection) and retry later
            const isRetryable =
              e?.message === 'Network Error' ||
              e?.code === 'ECONNABORTED' ||
              (typeof e?.message === 'string' &&
                e.message.startsWith('timeout')) ||
              (e?.response?.status >= 500)
            if (
              (title && title.startsWith('duplicate detected')) ||
              title === 'Resource not found'
            ) {
              //remove tmpReport and related issues from local db
              const result = await tmpIssues.find({
                selector: {
                  'report.localId': {
                    $eq: tmpReport._id
                  }
                }
              })
              await Promise.each(result.docs, async (issue) => {
                await deleteTmpIssue(issue)
              })
              await deleteTmpReport(tmpReport)
              console.warn('syncReportsJob report creation duplicate detected')
              return resolve()
            } else if (isRetryable) {
              tmpReport.status = 'FAILED'
              // last changes on current tmpReport, no need to update _rev
              tmpReports.put(tmpReport)
              console.warn(
                'syncReportsJob report creation failed due to a retryable error',
                e?.message
              )
              return resolve()
            }
            // other known errors here
            else {
              // remove tmpReport and related issues from local db
              const result = await tmpIssues.find({
                selector: {
                  'report.localId': {
                    $eq: tmpReport._id
                  }
                }
              })
              await Promise.each(result.docs, (issue) => {
                deleteTmpIssue(issue)
              })
              await deleteTmpReport(tmpReport)
              console.warn('syncReportsJob report creation failed')
              return resolve()
            }
          })
      })
    },

    processTmpIssue: async (tmpIssue, proxy, store) => {
      if (tmpIssue.wip) {
        // tmpIssue is already being processed
        if (!tmpIssue.lastAttempted) {
          tmpIssue.wip = false
          await tmpIssues.put(tmpIssue)
          return
        }

        const lastAttempted = dayjs(
          tmpIssue.lastAttempted,
          'DD/MM/YYYY HH:mm:ss'
        )
        const diff = dayjs().diff(lastAttempted, 'minute')
        if (diff > WIP_DURATION_LIMIT) {
          tmpIssue.wip = false
          await tmpIssues.put(tmpIssue)
          return
        }
      }

      tmpIssue.wip = true
      tmpIssue.lastAttempted = dayjs().format('DD/MM/YYYY HH:mm:ss')
      const result = await tmpIssues.put(tmpIssue)
      if (result.ok) {
        tmpIssue._rev = result.rev
      }

      if (tmpIssue.event === 'new') {
        await _commitTmpIssue(tmpIssue, proxy, store)
      } else if (tmpIssue.event === 'talk') {
        await _commitTmpTalks(tmpIssue, proxy)
      } else if (tmpIssue.event === 'close') {
        await _commitTmpClose(tmpIssue, proxy)
      } else if (tmpIssue.event === 'code') {
        await _commitTmpCode(tmpIssue, proxy)
      }
    },

    replicateIssues: (store, scopes, owner) => {
      if (replicationHandler !== null) {
        replicationHandler.cancel()
      }
      // NOTE: on met un setTimeout pour marquer la synchro comme DONE au delà de 10sec d'inactivité de Cloudant
      let timeoutHandler = setTimeout(() => {
        store.commit('reporter/SET_IS_LOADING_ISSUES', false)
      }, 10000)
      replicationHandler = localIssues.replicate
        .from(remoteIssues, {
          live: true,
          retry: true,
          batch_size: 40,
          batch_limit: 2,
          style: 'main_only',
          checkpoint: 'target',
          filter: 'remoteFilters/byScopesOrOwner',
          query_params: { scopes: scopes.join(','), owner: owner }
        })
        .on('change', function (change) {
          // NOTE: lors d'un activité de Cloudant, on remet le compteur de 10sec
          clearTimeout(timeoutHandler)
          timeoutHandler = setTimeout(() => {
            store.commit('reporter/SET_IS_LOADING_ISSUES', false)
          }, 10000)
          if (change.pending === 0) {
            // NOTE: quand la synchro est terminé, on clearTimeout
            clearTimeout(timeoutHandler)
            store.commit('reporter/SET_IS_LOADING_ISSUES', false)
          } else {
            store.commit('reporter/SET_IS_LOADING_ISSUES', true)
            store.commit('reporter/SET_LOADING_ISSUES_PERCENT', change.pending)
          }
        })
        .on('paused', function () {
          console.info('🚀 ~ paused on loading issues')
        })
        .on('error', function (err) {
          console.error('🚀 ~ error on loading issues', err.message)
        })
    },

    processTmpPlaces: (placesToProcess, proxy) => {
      return new Promise(async (resolve, reject) => {
        try {
          let places = (
            await proxy.loadAgencyResidenceOfPlaces(
              placesToProcess.map((place) => place.id)
            )
          ).data
          await app.config.globalProperties.$patrimoine.storePlaces(places)
          const promise = await tmpPlaces.bulkDocs(
            placesToProcess.map((place) => {
              place._deleted = true
              return place
            })
          )
          resolve(promise)
        } catch (error) {
          reject(error)
        }
      })
    },

    localOptions: () => {
      let options = { auto_compaction: true }
      if (isCordova) {
        PouchDB.plugin(sqlite)
        options = {
          auto_compaction: true,
          adapter: 'cordova-sqlite',
          location: 'default'
          // androidDatabaseImplementation: 2
        }
      }
      return options
    },
    initLocalPlaces: async () => {
      try {
        tmpPlaces = new PouchDB('placesIds.db', helpers.localOptions())
        tmpPlaces.createIndex({
          index: { fields: ['id'] }
        })
      } catch (err) {
        console.error('err initLocalPlaces', err)
      }
    },
    initLocalProviders: async () => {
      try {
      localProviders = new PouchDB('localProviders.db', helpers.localOptions())
        localProviders.createIndex({
          index: { fields: ['email'] }
        })
      } catch (err) {
        console.error('err initLocalProviders', err)
      }
    },
    initLocalUsers: async () => {
      try {
        localUsers = new PouchDB('localUsers.db', helpers.localOptions())
        localUsers.createIndex({
          index: { fields: ['email'] }
        })
      } catch (err) {
        console.error('err initLocalUsers', err)
      }
    }
  }

  const DB = {
    initLocal: (cordova) => {
      isCordova = cordova
      localIssues = new PouchDB('localCmpIssues.db', helpers.localOptions())
      localProviders = new PouchDB('localProviders.db', helpers.localOptions())
      localIssues.createIndex({
        index: {
          fields: ['meta.updatedAt']
        }
      })
      localProviders.createIndex({
        index: { fields: ['email'] }
      })
      tmpReports = new PouchDB('tmpReports.db', helpers.localOptions())
      tmpReports.createIndex({
        index: { fields: ['wip'] }
      })
      tmpReports.createIndex({
        index: { fields: ['status'] }
      })
      tmpReports.createIndex({
        index: { fields: ['audit.id', 'status'] }
      })
      tmpIssues = new PouchDB('tmpIssues.db', helpers.localOptions())
      tmpIssues.createIndex({
        index: { fields: ['wip'] }
      })
      localUsers = new PouchDB('localUsers.db', helpers.localOptions())
      localUsers.createIndex({
        index: { fields: ['email'] }
      })
      helpers.initLocalPlaces()
    },
    initDBSync(store, company, user) {
      // NOTE: Replicate issues DB from remote
      helpers.initRemote('cmp_' + company.id, 'dfhgs_dih!', 'cmp_' + company.id)
      helpers.replicateIssues(store, user.scopes, user.id)
      tmpReports.find({ selector: { wip: true } }).then((results) => {
        for (let tmpReport of results.docs) {
          tmpReport.wip = false
          tmpReports.put(tmpReport)
        }
      })

      tmpIssues.find({ selector: { wip: true } }).then((results) => {
        for (let tmpIssue of results.docs) {
          tmpIssue.wip = false
          tmpIssues.put(tmpIssue)
        }
      })
    },
    getItemsToSync: async () => {
      const reportsToSync = await new Promise((resolve) => {
        tmpReports
          .find({
            selector: {
              status: {
                $in: ['PENDING', 'FAILED'] // NOTE: we treat failed tmpItems as well
              }
            }
          })
          .then((result) => {
            return resolve(result.docs)
          })
      })
      const issuesToSync = await new Promise((resolve) => {
        tmpIssues.find({ selector: {} }).then((result) => {
          return resolve(result.docs)
        })
      })

      return reportsToSync.length + issuesToSync.length
    },
    syncTmpItems: async (store, proxy) => {
      const getReportsToSync = async () => {
        return new Promise((resolve) => {
          tmpReports
            .find({
              selector: {
                status: {
                  $in: ['IN_PROGRESS', 'PENDING', 'FAILED'] // NOTE: we treat failed tmpItems as well
                }
              }
            })
            .then((result) => {
              return resolve(result.docs)
            })
        })
      }

      const getIssuesToSync = async () => {
        return new Promise((resolve) => {
          tmpIssues.find({ selector: {} }).then((result) => {
            return resolve(result.docs)
          })
        })
      }

      const reportsToSync = await getReportsToSync()
      const issuesToSync = await getIssuesToSync()

      if (reportsToSync.length + issuesToSync.length === 0) {
        store.dispatch('common/clearPendingReports')
        store.dispatch('common/clearPendingIssues')
        await NotificationService.clearNotifications()
        return
      }

      const syncTmpReports = async (reportsToSync) => {
        return new Promise(async (resolve, reject) => {
          // NOTE: send tmpReports
          if (reportsToSync.length === 0) {
            store.dispatch('common/clearPendingReports')
            return resolve()
          } else {
            store.dispatch('common/setPendingReports', reportsToSync.length)
            try {
              await Promise.each(reportsToSync, async (tmpReport) => {
                await helpers.processTmpReport(tmpReport, proxy, store)
              })
              store.dispatch('common/clearPendingReports')
              return resolve()
            } catch (e) {
              console.error(
                'syncReportsJob reports creation duplicate detected:',
                e
              )
              return reject(e)
            }
          }
        })
      }

      try {
        await syncTmpReports(reportsToSync)

        const issuesToSync = await getIssuesToSync()

        await syncTmpIssues(issuesToSync, proxy, store)
      } catch (err) {
        console.error('syncTmpItems err', err)
      } finally {
        const reportsToSync = await new Promise((resolve) => {
          tmpReports
            .find({
              selector: {
                status: {
                  $in: ['PENDING', 'FAILED'] // NOTE: we treat failed tmpItems as well
                }
              }
            })
            .then((result) => {
              return resolve(result.docs)
            })
        })
        store.dispatch('common/setPendingReports', reportsToSync.length)
        const issuesToSync = await new Promise((resolve) => {
          tmpIssues.find({ selector: {} }).then((result) => {
            return resolve(result.docs)
          })
        })
        store.dispatch('common/setPendingIssues', issuesToSync.length)
      }
    },
    loadMissingPlaces: async (store, proxy) => {
      let consecutiveErrorsCount = 0

      try {
        let result = await tmpPlaces.find({
          selector: {},
          sort: ['id']
        })
        let storedPlaces = result.docs
        store.dispatch('reporter/setPlacesToLoadCount', storedPlaces.length)
        if (storedPlaces.length === 0) {
          LoadingBar.stop()
          return app.config.globalProperties.$cron.stop('syncUserPlacesJob')
        }

        const isLoadingPlace = store.getters['reporter/isLoadingPlace']
        if (isLoadingPlace) {
          return LoadingBar.stop()
        }

        store.commit('reporter/SET_IS_LOADING_PLACES', true)

        Notify.create({
          message: t('common.placeLoading'),
          caption: t('common.placeLoadingCaption'),
          icon: 'info',
          type: 'info',
          position: 'top'
        })
        LoadingBar.start(2000)

        const chunks = chunk(storedPlaces, 100)

        const target =
          (
            await app.config.globalProperties.$patrimoine
              .placesCollection()
              .allDocs()
          ).rows.length + storedPlaces.length

        for (let i = 0; i < chunks.length; i++) {
          const chunk = chunks[i]
          try {
            await helpers.processTmpPlaces(chunk, proxy)
            LoadingBar.increment(chunk.length / storedPlaces.length)
            const placesInDB = (
              await app.config.globalProperties.$patrimoine
                .placesCollection()
                .allDocs()
            ).rows
            if (target <= placesInDB.length) {
              Notify.create({
                message: t('common.placeLoaded'),
                icon: 'check',
                type: 'positive',
                position: 'top'
              })
              LoadingBar.stop()
            }
            consecutiveErrorsCount = 0
          } catch (err) {
            console.error('Error storing tmpPlace', err)
            consecutiveErrorsCount++
            if (consecutiveErrorsCount > MAX_CONSECUTIVE_ERRORS) {
              Notify.create({
                message: t('common.placeloadingError'),
                caption: t('common.placeloadingErrorCaption'),
                icon: 'warning',
                type: 'warning',
                position: 'top'
              })
              LoadingBar.stop()
              throw 'Error deleting tmpPlace'
            }
          }
        }
      } catch (err) {
        console.error('loadMissingPlaces', err)
      } finally {
        store.commit('reporter/SET_IS_LOADING_PLACES', false)
      }
    },
    resetLocalPlaces: async () => {
      await DB.emptyDB(tmpPlaces)
      helpers.initLocalPlaces()
    },
    fillTmpPlaces: async (places) => {
      await tmpPlaces.bulkDocs(places)
    },
    fillLocalProviders: async (providers) => {
      await DB.emptyDB(localProviders)
      helpers.initLocalProviders()
      await localProviders.bulkDocs(providers)
    },
    localIssues: () => localIssues,
    tmpReports: () => tmpReports,
    tmpIssues: () => tmpIssues,
    tmpPlaces: () => tmpPlaces,
    localProviders: () => localProviders,
    localUsers: () => localUsers,
    emptyDB: async (db) => {
      try {
        await db.destroy()
      } catch (err) {
        console.error('err destroy', {
          db: db.name,
          err
        })
      }
    },
    resetTmpItems: async () => {
      await DB.emptyDB(tmpReports)
      await DB.emptyDB(tmpIssues)
      tmpReports = new PouchDB('tmpReports.db', helpers.localOptions())
      tmpIssues = new PouchDB('tmpIssues.db', helpers.localOptions())
    },
    resetLocalDBs: async (isCordova) => {
      await DB.emptyDB(tmpReports)
      await DB.emptyDB(tmpIssues)
      await DB.emptyDB(localIssues)
      await DB.emptyDB(localProviders)
      await DB.emptyDB(tmpPlaces)

      DB.initLocal(isCordova)
    },
    replicateIssues: async (store, scopes, owner) => {
      helpers.replicateIssues(store, scopes, owner)
    },
    stopReplication: () => {
      if (replicationHandler !== null) {
        replicationHandler.cancel()
      }
    },
    getTasksList: async (store) => {
      const tmpReportsStored = await tmpReports.find({
        selector: {
          status: {
            $in: ['IN_PROGRESS', 'PENDING', 'FAILED'] // NOTE: we treat failed tmpItems as well
          }
        }
      })
      const tmpIssuesStored = await tmpIssues.find({
        selector: {}
      })

      const categories = store.getters['company/categories']
      const subCategories = categories.map((category) => category.childs).flat()
      tmpReportsStored.docs = tmpReportsStored.docs.map((doc) => {
        const auditByCategory = subCategories.find(
          (category) => category.id === doc.audit.checklist.category.id
        )
        doc.place = {
          name: doc.audit?.place?.name || t('common.placeAbsent')
        }
        doc.checklist = {
          name: doc.audit?.checklist?.name
        }
        doc.img = auditByCategory?.img || undefined
        return doc
      })

      tmpIssuesStored.docs = tmpIssuesStored.docs.map((doc) => {
        if (doc.event === 'new') {
          const spotByCategory = subCategories.find(
            (category) => category.id === doc.category.id
          )
          doc.place = {
            name: doc?.place?.name || t('common.placeAbsent')
          }
          doc.img = spotByCategory?.img || undefined
        }
        return doc
      })
      let result = [...tmpReportsStored.docs, ...tmpIssuesStored.docs]
      result = orderBy(result, ['scheduledAt'], ['desc'])
      return result
    },
    pendingTmpItems: async () => {
      const reports = await tmpReports
        .find({
          selector: {
            status: {
              $in: ['PENDING', 'FAILED', 'IN_PROGRESS']
            }
          }
        })
        .then((result) => {
          return result.docs
            .filter((doc) => doc.status) // send only report type row
            .map((doc) => {
              const issues = (doc.issues || []).map((issue) => {
                return {
                  ...issue,
                  _id: issue._id.split(':')[0],
                  _rev: undefined
                }
              })
              return {
                ...doc,
                issues,
                _rev: undefined
              }
            })
        })
      const issues = await tmpIssues
        .find({
          selector: {}
        })
        .then((result) => {
          return result.docs
            .filter((doc) => doc.event) // send only issue type row
            .map((doc) => {
              return {
                ...doc,
                _id: doc._id.split(':')[0],
                _rev: undefined,
                wip: undefined
              }
            })
        })

      return { reports, issues }
    },
    fillLocalUsers: async (users) => {
      await DB.emptyDB(localUsers)
      helpers.initLocalUsers()
      await localUsers.bulkDocs(users)
    },
  }
  app.config.globalProperties.$DB = DB
  app.provide('$DB', DB)
})
